import ReactECharts from 'echarts-for-react';

/**
 * ProcessGraphChart_3 — "dark command-deck" variant of ProcessGraphChart (blog06).
 * Same directed process graph with an approval gate, same nodes/edges/positions/
 * sizes — zero invented data. Opaque slate-900 background, light text, teal/cyan
 * accents. Confident dashboard feel.
 */

const PALETTE = {
  teal: '#0AB3BF',
  cyan: '#4DD4E0',
  slateLight: '#94A3B8',
  slateMid: '#64748B',
  edge: '#334155', // slate-700 — subtle on slate-900
  text: '#F8FAFC',
  textSoft: '#94A3B8',
  surface: '#0F172A', // slate-900, opaque
};

const CATEGORIES = [
  { name: 'Step', itemStyle: { color: PALETTE.teal } },
  { name: 'Gate', itemStyle: { color: PALETTE.cyan } },
  { name: 'External', itemStyle: { color: PALETTE.slateLight } },
  { name: 'Log', itemStyle: { color: PALETTE.slateMid } },
];

const NODES = [
  { name: 'Qualify lead', category: 0, x: 40, y: 40, symbolSize: 64 },
  { name: 'Draft outreach', category: 0, x: 40, y: 150, symbolSize: 64 },
  { name: 'Human review gate', category: 1, x: 40, y: 260, symbolSize: 76 },
  { name: 'Send email', category: 2, x: 40, y: 370, symbolSize: 68 },
  { name: 'Log the run', category: 3, x: 40, y: 480, symbolSize: 64 },
];

const EDGES = [
  { source: 'Qualify lead', target: 'Draft outreach' },
  { source: 'Draft outreach', target: 'Human review gate' },
  { source: 'Human review gate', target: 'Send email', lineStyle: { color: PALETTE.cyan } },
  { source: 'Send email', target: 'Log the run', lineStyle: { color: PALETTE.slateMid } },
];

/** ONE shared name formatter — tooltip, node labels, and legend all use it. */
const formatName = (name: string) => name;

const LEGEND_LABELS: Record<string, string> = {
  Step: 'Step (bot)',
  Gate: 'Approval gate',
  External: 'External action',
  Log: 'Log row',
};

export const ProcessGraphChart_3 = ({ height = 440 }: { height?: number }) => {
  const option = {
    backgroundColor: PALETTE.surface,
    tooltip: {
      backgroundColor: '#1E293B',
      borderColor: PALETTE.slateMid,
      textStyle: { color: PALETTE.text, fontSize: 13 },
      formatter: (p: { name: string }) => formatName(p.name),
    },
    legend: {
      top: 8,
      textStyle: { color: PALETTE.text, fontSize: 12 },
      inactiveColor: PALETTE.slateMid,
      formatter: (name: string) => LEGEND_LABELS[formatName(name)] ?? formatName(name),
    },
    series: [
      {
        type: 'graph',
        layout: 'none',
        roam: true,
        draggable: true,
        data: NODES,
        links: EDGES,
        categories: CATEGORIES,
        symbol: 'circle',
        label: {
          show: true,
          position: 'bottom',
          color: PALETTE.textSoft,
          fontSize: 12,
          fontWeight: 600,
          formatter: (p: { name: string }) => formatName(p.name),
        },
        lineStyle: { color: PALETTE.edge, width: 2, curveness: 0.08 },
        edgeSymbol: ['none', 'arrow'],
        edgeSymbolSize: 10,
        emphasis: { focus: 'adjacency', lineStyle: { width: 4 } },
      },
    ],
  };

  return (
    <div className="my-8 rounded-xl overflow-hidden border border-slate-700 bg-slate-900 p-2">
      <ReactECharts option={option} style={{ height }} opts={{ renderer: 'canvas' }} />
      <p className="mt-2 text-xs text-slate-400 text-center">
        Figure 1. A process is a directed graph: bot steps feed a human review gate, and only an approved action crosses
        to the outside world — then the whole run is logged. Drag to reposition; the edge to the gate is the decision.
      </p>
    </div>
  );
};
